import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { fetchWithAuth } from '../utils/api';
import { API_BASE_URL } from '../config/constants';
import { convertUTCToLocalFormString, convertToUTCString, formatDateTimeForDisplay } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/auth';
import { DateTime } from 'luxon'; // Luxonをインポート
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

interface Client {
  id: number;
  name: string;
}

interface Project {
  id: number;
  name: string;
  client?: Client;
}

interface Schedule {
  id: number;
  employee: { id: number; name: string };
  project?: Project; // プロジェクトは任意のため省略可能に変更
  title?: string;    // タイトルを追加（任意）
  task: string;
  isCompleted: boolean; // 完了状態のフラグを追加
  startTime: string;
  endTime: string;
  createdBy?: { 
    id: number; 
    name: string;
  }; // 作成者情報を明示的に型定義
}

interface ScheduleFormModalProps {
  userRole: UserRole;
  initialData?: Schedule | null;
  employeeId?: number;
  employeeName?: string;
  date?: string;
  onClose: () => void;
  onScheduleUpdated?: () => void;
  onScheduleAdded?: () => void;
  onScheduleDeleted?: () => void;
}

// ユーティリティ関数: UTCの日時文字列をフォーム用のローカル時間に変換
const formatDateForInput = (dateString: string) => {
  if (!dateString) return '';
  
  console.log('フォーマット前のUTC日時:', dateString);
  
  // 新しいformatDateTimeForDisplay関数を使用してログ表示を強化
  console.log('UTC日時の表示形式:', formatDateTimeForDisplay(dateString));
  
  // UTCの日時文字列をJSTのフォーム入力用に変換
  const localDateString = convertUTCToLocalFormString(dateString);
  console.log('フォーマット後の日時(JST):', localDateString);
  return localDateString;
};

const ScheduleFormModal: React.FC<ScheduleFormModalProps> = ({
  userRole,
  initialData = null,
  employeeId,
  employeeName,
  date,
  onClose,
  onScheduleUpdated,
  onScheduleAdded,
  onScheduleDeleted,
}) => {
  const { canEditSchedules, canViewScheduleDetails } = useAuth();
  
  // 実際の権限を確認
  const canEdit = canEditSchedules();
  const canView = canViewScheduleDetails();
  
  // スケジュール閲覧者または checker の場合は、詳細を表示するが編集は不可
  const isReadOnly = !canEdit;
  
  // スケジュール空き状況確認者の場合は詳細を非表示
  const hideDetails = !canView;
  
  // タイトルと作業内容のstate
  const [title, setTitle] = useState(initialData?.title || '');
  const [task, setTask] = useState(initialData?.task || '');
  
  // 完了状態のstate
  const [isCompleted, setIsCompleted] = useState(initialData?.isCompleted || false);
  
  // 開始・終了時刻の初期値設定
  const [startTime, setStartTime] = useState(() => {
    // 初期データがある場合はUTC→JSTに変換して使用
    if (initialData?.startTime) {
      console.log('初期開始時間(UTC):', initialData.startTime);
      return formatDateForInput(initialData.startTime);
    }
    
    // 新規作成時は選択された日付と標準時間を組み合わせる
    if (date) {
      // デフォルト時刻を設定 (午前8時)
      return `${date}T08:00`;
    }
    
    return '';
  });
  
  const [endTime, setEndTime] = useState(() => {
    // 初期データがある場合はUTC→JSTに変換して使用
    if (initialData?.endTime) {
      console.log('初期終了時間(UTC):', initialData.endTime);
      return formatDateForInput(initialData.endTime);
    }
    
    // 新規作成時は選択された日付と標準時間を組み合わせる
    if (date) {
      // デフォルト時刻を設定 (午後5時)
      return `${date}T17:00`;
    }
    
    return '';
  });
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(true); // プロジェクト読み込み状態を追加
  const [projectError, setProjectError] = useState<string | null>(null); // プロジェクト読み込みエラーを追加

  const fetchProjects = useCallback(async () => {
    console.log('プロジェクト一覧の取得を開始...');
    setLoadingProjects(true);
    setProjectError(null);
    
    try {
      // ログ: API呼び出し開始
      const startTime = performance.now();
      console.log(`API呼び出し開始: ${API_BASE_URL}/projects`);
      
      const response = await fetchWithAuth(`${API_BASE_URL}/projects`);
      
      // ログ: API呼び出し完了
      const endTime = performance.now();
      console.log(`API呼び出し完了 (${Math.round(endTime - startTime)}ms), 状態: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        throw new Error(`プロジェクト一覧の取得に失敗しました (${response.status} ${response.statusText})`);
      }
      
      const data = await response.json();
      console.log(`プロジェクトデータ取得成功: ${data.length}件`);
      setProjects(data);
  
      if (initialData?.project) {
        console.log(`初期プロジェクトID: ${initialData.project.id}の検索を開始`);
        const project = data.find((p: Project) => p.id === initialData.project?.id);
        
        if (project) {
          console.log(`初期プロジェクト "${project.name}" を設定しました`);
          setSelectedProject(project);
        } else {
          console.warn(`警告: ID ${initialData.project.id}のプロジェクトが見つかりませんでした`);
        }
      }
    } catch (error) {
      console.error('プロジェクト一覧の取得中にエラー:', error);
      setProjectError(error instanceof Error ? error.message : '不明なエラー');
    } finally {
      setLoadingProjects(false);
      console.log('プロジェクトデータの取得処理が完了しました');
    }
  }, [initialData]); // initialDataが依存関係として必要
  
  useEffect(() => {
    console.log('ScheduleFormModal: マウント時のプロジェクト取得を実行');
    fetchProjects();
    
    return () => {
      console.log('ScheduleFormModal: アンマウント');
    };
  }, [fetchProjects]);

  // コンソールにデバッグ情報を出力（開発時のみ）
  useEffect(() => {
    if (initialData) {
      console.log('Schedule modal initial data:', initialData);
      console.log('Creator info:', initialData.createdBy);
    }
  }, [initialData]);

  // 完了状態の切り替え処理
  const handleToggleCompletion = async () => {
    if (!initialData || isReadOnly) return;
    
    try {
      const url = `${API_BASE_URL}/schedules/${initialData.id}/toggle-completion`;
      
      const response = await fetchWithAuth(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error('完了状態の切り替えに失敗しました');
      }
      
      const updatedSchedule = await response.json();
      
      // ローカルのstate更新
      setIsCompleted(updatedSchedule.isCompleted);
      
      // 親コンポーネントに通知
      onScheduleUpdated?.();
      
      // モーダルを閉じる - 自動保存して閉じる改善
      onClose();
      
      // 成功通知を表示 (オプション)
      console.log(`スケジュールを${updatedSchedule.isCompleted ? '完了' : '未完了'}に更新しました`);
    } catch (error) {
      console.error('完了状態の切り替えに失敗しました:', error);
      // エラー時はモーダルを閉じずにエラーメッセージを表示することもできます
    }
  };

  // フォーム送信処理
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    // 編集権限がなければ何もしない
    if (isReadOnly) return;

    try {
      // APIエンドポイント決定
      const url = initialData
        ? `${API_BASE_URL}/schedules/${initialData.id}`
        : `${API_BASE_URL}/schedules`;
      const method = initialData ? 'PUT' : 'POST';

      // JSTのフォーム入力をUTCに変換
      console.log('フォーム入力値(JST):');
      console.log('  開始時間:', startTime);
      console.log('  終了時間:', endTime);
      
      // JST→UTC変換 - convertToUTCString関数を使用
      const utcStartTime = convertToUTCString(startTime);
      const utcEndTime = convertToUTCString(endTime);
      
      console.log('バックエンドへ送信するUTC時間:');
      console.log('  UTC開始時間:', utcStartTime);
      console.log('  UTC終了時間:', utcEndTime);
      
      // 変換されたUTC日時を読みやすい形式で表示
      console.log('  UTC開始時間(表示形式):', formatDateTimeForDisplay(utcStartTime));
      console.log('  UTC終了時間(表示形式):', formatDateTimeForDisplay(utcEndTime));

      // リクエスト送信
      const response = await fetchWithAuth(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employeeId: initialData?.employee.id || employeeId,
          projectId: selectedProject?.id,
          title,
          task,
          isCompleted, // 完了状態を追加
          startTime: utcStartTime,
          endTime: utcEndTime,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTPエラー: ${response.status} ${response.statusText}`);
      }

      // 成功時の処理
      if (initialData) {
        onScheduleUpdated?.();
      } else {
        onScheduleAdded?.();
      }

      onClose();
    } catch (error) {
      console.error('スケジュールの保存に失敗しました', error);
    }
  };

  const handleDelete = async () => {
    // 編集権限がなければ何もしない
    if (isReadOnly || !initialData) return;

    if (!window.confirm('この予定を削除しますか？')) {
      return;
    }

    try {
      const url = `${API_BASE_URL}/schedules/${initialData.id}`;
      const response = await fetchWithAuth(url, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('削除に失敗しました');
      }

      onScheduleDeleted?.();
      onClose();
    } catch (error) {
      console.error('予定の削除に失敗しました', error);
    }
  };

  // プロジェクト読み込み中かつ編集者以上の権限があるなら読み込み中表示
  if (loadingProjects) {
    console.log('プロジェクトを読み込み中...');
    return (
      <Dialog open={true} onClose={onClose}>
        <DialogTitle>データ読み込み中...</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} sx={{ mr: 1 }} />
            <Typography>プロジェクト情報を読み込んでいます...</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>閉じる</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // プロジェクト読み込みエラーの場合
  if (projectError) {
    console.log('プロジェクト読み込みエラー:', projectError);
    return (
      <Dialog open={true} onClose={onClose}>
        <DialogTitle>エラー</DialogTitle>
        <DialogContent>
          <Typography color="error">
            プロジェクト情報の読み込みに失敗しました: {projectError}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => fetchProjects()}>再試行</Button>
          <Button onClick={onClose}>閉じる</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // 表示タイトルの決定
  let modalTitle = initialData ? 'スケジュールの詳細' : '新規スケジュール';
  if (canEdit) {
    modalTitle = initialData ? 'スケジュールの編集' : 'スケジュールの追加';
  }

  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>{modalTitle}</DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          {/* タイトル入力フィールド（任意） */}
          <TextField
            label="現場名"
            value={hideDetails ? '予約済み' : title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            margin="normal"
            disabled={isReadOnly || hideDetails}
          />
          
          {/* 作業内容（必須） */}
          <TextField
            label="作業内容"
            value={hideDetails ? '予約済み' : task}
            onChange={(e) => setTask(e.target.value)}
            required
            fullWidth
            margin="normal"
            disabled={isReadOnly || hideDetails}
          />
          
          {/* 開始・終了時間（変更なし） */}
          <TextField
            label="開始時間"
            type="datetime-local"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            required
            fullWidth
            margin="normal"
            disabled={isReadOnly}
          />
          <TextField
            label="終了時間"
            type="datetime-local"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            required
            fullWidth
            margin="normal"
            disabled={isReadOnly}
          />
          
          {/* 完了状態スイッチ - 編集者かつ既存スケジュールのみ表示 */}
          {initialData && !hideDetails && (
            <Box 
              sx={{ 
                mt: 2, 
                mb: 2,
                p: 1,
                border: isCompleted ? '1px solid #4caf50' : '1px solid #e0e0e0',
                borderRadius: 1,
                bgcolor: isCompleted ? '#e8f5e9' : 'transparent', 
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={isCompleted}
                    onChange={handleToggleCompletion}
                    disabled={isReadOnly}
                    color="success"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {isCompleted ? 
                      <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} /> : 
                      <RadioButtonUncheckedIcon sx={{ mr: 1 }} />
                    }
                    <Typography>
                      {isCompleted ? '完了済み' : '未完了'}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}
          
          {/* 作成者情報の表示 (閲覧専用) */}
          {initialData && initialData.createdBy && canView && (
            <Box 
              sx={{ 
                mt: 2, 
                mb: 2, 
                p: 1.5, 
                bgcolor: '#fafafa', 
                border: '1px solid #e0e0e0', 
                borderLeft: '3px solid #9e9e9e',
                borderRadius: 1 
              }}
            >
              <Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}>
                作成者: <span style={{ fontWeight: '500', marginLeft: '4px' }}>{initialData.createdBy.name}</span>
              </Typography>
            </Box>
          )}

          {/* 権限に基づいたボタン表示 */}
          {canEdit ? (
            <DialogActions>
              <Button type="submit">{initialData ? '更新' : '追加'}</Button>
              {initialData && (
                <Button
                  type="button"
                  onClick={handleDelete}
                  style={{ backgroundColor: 'red', color: 'white' }}
                >
                  削除
                </Button>
              )}
              <Button type="button" onClick={onClose}>
                キャンセル
              </Button>
            </DialogActions>
          ) : (
            <DialogActions>
              <Button type="button" onClick={onClose}>
                閉じる
              </Button>
            </DialogActions>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleFormModal;


